import React, { useEffect, useRef, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { SummaryListActionConfig, ComponentType } from "../../services/types/ComponentTypes";
import { fileService, isImageId, isFileKey, openImageById } from "../../services/fileService";
import { isComponentVisible } from "../../utils/visibilityUtils";

interface WorkflowAction {
  Name: string;
  Label: string;
  Action: string;
  States?: string[];
  EnableOn?: string[];
  Screen?: string;
  Action_ref_column?: string;
}

// Get summary action icon (same as DataTable - using filled icons)
const getSummaryActionIcon = (
  actionType: string,
  size: number
): React.ReactNode => {
  switch (actionType) {
    case "edit":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
        </svg>
      );
    case "delete":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
        </svg>
      );
    case "pdf_download":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
        </svg>
      );
    case "view_report":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
        </svg>
      );
    case "lock":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      );
    case "print":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
      );
    case "approve":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      );
    default:
      return null;
  }
};

interface CardViewProps {
  data: Record<string, any>[];
  visibleFields: Array<{
    Label?: string;
    BindValue?: string;
    Json_Attribute?: string;
    ControlType?: string;
    componentType?: ComponentType;
  }>;
  workflowActions: WorkflowAction[];
  summaryActions?: SummaryListActionConfig[];
  onActionTrigger?: (action: string, row: Record<string, any>, workflowItem?: WorkflowAction) => void;
  onRowClick?: (row: Record<string, any>, index: number) => void;
  selectedRowIndex?: number | null;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  totalCount?: number;
  useServerSidePagination?: boolean;
  onProgressBarClick?: (row: Record<string, any>, element: HTMLElement) => void;
  filters?: Record<string, string>;
  onFilterChange?: (key: string, value: string) => void;
}

export const CardView: React.FC<CardViewProps> = ({
  data,
  visibleFields,
  workflowActions,
  summaryActions = [],
  onActionTrigger,
  onRowClick,
  selectedRowIndex = null,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
  totalCount = 0,
  useServerSidePagination = false,
  onProgressBarClick,
  filters = {},
  onFilterChange,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const progressBarRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  
  // Ensure data and arrays are valid
  const safeData = Array.isArray(data) ? data : [];
  const safeVisibleFields = Array.isArray(visibleFields) ? visibleFields : [];
  const safeWorkflowActions = Array.isArray(workflowActions) ? workflowActions : [];
  
  // State for detail drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState<Record<string, any> | null>(null);
  
  // State for search functionality
  const [selectedSearchField, setSelectedSearchField] = useState<string>("");
  const [localSearchValue, setLocalSearchValue] = useState<string>("");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const userSelectedFieldRef = useRef<boolean>(false); // Track if user manually selected a field
  const selectedSearchFieldRef = useRef<string>(""); // Track current value to avoid stale closures
  const initializedRef = useRef<boolean>(false); // Track if initialization has completed
  
  // Get the active search field from filters (for filtering logic only)
  const activeSearchField = useMemo(() => {
    if (filters && Object.keys(filters).length > 0) {
      const firstFilterKey = Object.keys(filters).find(key => filters[key]);
      if (firstFilterKey) {
        return firstFilterKey;
      }
    }
    // Default to first field if available
    if (safeVisibleFields.length > 0) {
      const firstField = safeVisibleFields[0];
      return firstField?.Json_Attribute || firstField?.BindValue || "";
    }
    return "";
  }, [filters, safeVisibleFields]);
  
  // Helper function to get field value from item by key (handles key variations for filtering)
  const getItemValueByKey = useCallback((item: Record<string, any>, fieldKey: string): any => {
    if (!fieldKey || !item) return "";
    
    // Try exact key first
    if (item[fieldKey] !== undefined && item[fieldKey] !== null) {
      return item[fieldKey];
    }
    
    // Try case variations
    const lowerKey = fieldKey.toLowerCase();
    if (item[lowerKey] !== undefined && item[lowerKey] !== null) {
      return item[lowerKey];
    }
    
    const upperKey = fieldKey.toUpperCase();
    if (item[upperKey] !== undefined && item[upperKey] !== null) {
      return item[upperKey];
    }
    
    // Try camelCase variations
    const camelKey = fieldKey.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    if (item[camelKey] !== undefined && item[camelKey] !== null) {
      return item[camelKey];
    }
    
    return "";
  }, []);

  // Filter data based on filters (client-side filtering when not using server-side pagination)
  const filteredData = useMemo(() => {
    // If server-side pagination is enabled, return data as-is (server handles filtering)
    if (useServerSidePagination) {
      return safeData;
    }

    // Client-side filtering - match DataTable's exact logic
    let filtered = [...safeData];
    
    // Only filter by the active search field
    if (activeSearchField && filters?.[activeSearchField]) {
      const searchValue = filters[activeSearchField];
      
      // Filter using the same approach as DataTable
      filtered = filtered.filter((item) => {
        // Use exact key match first (like DataTable does)
        const itemValue = item[activeSearchField];
        
        // If not found, try helper function for key variations
        const finalValue = itemValue !== undefined && itemValue !== null 
          ? itemValue 
          : getItemValueByKey(item, activeSearchField);
        
        // Match DataTable's filtering logic exactly
        return String(finalValue).toLowerCase().includes(searchValue.toLowerCase());
      });
    }
    
    return filtered;
  }, [safeData, filters, useServerSidePagination, activeSearchField, getItemValueByKey]);

  // Get workflow status for a row
  const getWorkflowStatus = (row: Record<string, any>) => {
    const lastAction =
      row?.last_action ??
      row?.lastAction ??
      row?.LastAction ??
      "";

    const lastStateRaw =
      row?.last_state ??
      row?.lastState ??
      row?.LastState ??
      row?.workflow_state ??
      row?.workflowState;

    const normalizedLastState =
      typeof lastStateRaw === "string"
        ? lastStateRaw.trim().toLowerCase()
        : lastStateRaw !== undefined && lastStateRaw !== null
          ? String(lastStateRaw).trim().toLowerCase()
          : "";

    const currentStateRaw =
      row?.current_state ??
      row?.currentState ??
      row?.CurrentState ??
      "";

    const normalizedCurrentState =
      typeof currentStateRaw === "string"
        ? currentStateRaw.trim().toLowerCase()
        : currentStateRaw !== undefined && currentStateRaw !== null
          ? String(currentStateRaw).trim().toLowerCase()
          : "";

    const isWorkflowClosed = normalizedCurrentState === "closed" || normalizedLastState === "closed";

    const actionRefColumnMap: Record<string, string> = {
      "Estimate": "estimate_id",
      "Inspect": "inspection_id",
      "Approve": "approved_id",
      "Job card": "jobcard_id",
      "QC Approve": "qc_approved_id",
      "Create invoice": "invoice_id",
      "Close": "closed_id",
    };

    const completedWorkflowIndices: number[] = [];

    safeWorkflowActions.forEach((workflowItem, idx) => {
      const actionMatch = lastAction &&
        String(lastAction).trim().toLowerCase() ===
        String(workflowItem.Action).trim().toLowerCase();

      const refColumnName = workflowItem.Action_ref_column ||
        actionRefColumnMap[workflowItem.Action] ||
        `${workflowItem.Action.toLowerCase().replace(/\s+/g, '_')}_id`;

      const refId = row[refColumnName] ||
        row[refColumnName.toLowerCase()] ||
        row[refColumnName.toUpperCase()] ||
        row[`${refColumnName}_id`] ||
        row[`${refColumnName.toLowerCase()}_id`] ||
        (workflowItem.Action === "Close" ? (row["job_close_id"] || row["job_closeId"] || row["JOB_CLOSE_ID"]) : undefined);

      const hasRefId = refId !== undefined &&
        refId !== null &&
        String(refId).trim() !== "";

      if (actionMatch && hasRefId) {
        completedWorkflowIndices.push(idx);
      }
    });

    const currentWorkflowIndex = completedWorkflowIndices.length > 0
      ? Math.max(...completedWorkflowIndices)
      : -1;

    if (safeWorkflowActions.length > 0) {
      const lastWorkflowIndex = safeWorkflowActions.length - 1;
      const lastWorkflowItem = safeWorkflowActions[lastWorkflowIndex];
      const lastRefColumnName = lastWorkflowItem.Action_ref_column ||
        actionRefColumnMap[lastWorkflowItem.Action] ||
        `${lastWorkflowItem.Action.toLowerCase().replace(/\s+/g, '_')}_id`;

      const lastRefId = row[lastRefColumnName] ||
        row[lastRefColumnName.toLowerCase()] ||
        row[lastRefColumnName.toUpperCase()] ||
        row[`${lastRefColumnName}_id`] ||
        row[`${lastRefColumnName.toLowerCase()}_id`] ||
        (lastWorkflowItem.Action === "Close" ? (row["job_close_id"] || row["job_closeId"] || row["JOB_CLOSE_ID"]) : undefined);

      const lastHasRefId = lastRefId !== undefined &&
        lastRefId !== null &&
        String(lastRefId).trim() !== "";

      if (lastHasRefId || isWorkflowClosed) {
        for (let i = 0; i <= lastWorkflowIndex; i++) {
          if (!completedWorkflowIndices.includes(i)) {
            completedWorkflowIndices.push(i);
          }
        }
      }
    }

    const finalCurrentWorkflowIndex = completedWorkflowIndices.length > 0
      ? Math.max(...completedWorkflowIndices)
      : -1;

    if (finalCurrentWorkflowIndex !== -1) {
      for (let i = 0; i <= finalCurrentWorkflowIndex; i++) {
        if (!completedWorkflowIndices.includes(i)) {
          completedWorkflowIndices.push(i);
        }
      }
    }

    const nextWorkflowIndex =
      finalCurrentWorkflowIndex !== -1 && finalCurrentWorkflowIndex < safeWorkflowActions.length - 1
        ? finalCurrentWorkflowIndex + 1
        : -1;

    return {
      completedIndices: completedWorkflowIndices,
      currentIndex: finalCurrentWorkflowIndex,
      nextIndex: nextWorkflowIndex,
    };
  };

  // Get step status
  const getStepStatus = (
    index: number,
    completedIndices: number[],
    currentIndex: number,
    nextIndex: number
  ): "completed" | "active" | "pending" => {
    if (completedIndices.includes(index)) {
      return "completed";
    }
    if (index === nextIndex) {
      return "active";
    }
    return "pending";
  };

  // Calculate progress percentage
  const getProgressPercentage = (completedIndices: number[], totalSteps: number): number => {
    if (totalSteps === 0) return 0;
    return Math.round((completedIndices.length / totalSteps) * 100);
  };

  // Get step icon
  const getStepIcon = (status: "completed" | "active" | "pending", label: string): React.ReactNode => {
    if (status === "completed") {
      return (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
        </svg>
      );
    }
    if (status === "active") {
      // Return icon based on label
      if (label.toLowerCase().includes("estimate")) {
        return (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      }
      if (label.toLowerCase().includes("job card") || label.toLowerCase().includes("jobcard")) {
        return (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      }
    }
    return null;
  };

  // Get field value
  const getFieldValue = (row: Record<string, any>, field: { BindValue?: string; Json_Attribute?: string }): string => {
    const jsonAttr = field.Json_Attribute || field.BindValue || "";
    return row[jsonAttr] || row[jsonAttr.toLowerCase()] || row[jsonAttr.toUpperCase()] || "";
  };

  // Handle card click to open drawer
  const handleCardClick = useCallback((row: Record<string, any>, index: number) => {
    setDrawerData(row);
    setDrawerOpen(true);
    // Also trigger the existing onRowClick if provided
    onRowClick?.(row, index);
  }, [onRowClick]);

  // Handle drawer close
  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    // Keep drawerData for animation, clear after transition
    setTimeout(() => setDrawerData(null), 300);
  }, []);

  // Format value for display in drawer
  const formatDisplayValue = useCallback((value: any): string => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }, []);

  // Render field value with attachment support
  const renderFieldValue = useCallback((row: Record<string, any>, field: { BindValue?: string; Json_Attribute?: string; componentType?: ComponentType; Label?: string }): React.ReactNode => {
    const value = getFieldValue(row, field);
    const isAttachment = field.componentType === ComponentType.FILES;
    
    if (isAttachment && value) {
      const handleAttachmentClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!value) return;
        try {
          if (isImageId(value)) {
            await openImageById(value);
          } else if (isFileKey(value)) {
            await fileService.openFile(value);
          } else {
            window.open(value, '_blank', 'noopener,noreferrer');
          }
        } catch (err: any) {
          alert(err?.message || 'Failed to open attachment.');
        }
      };
      return (
        <button
          onClick={handleAttachmentClick}
          className="inline-flex items-center justify-center p-1 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none"
          title={`View ${field.Label || 'attachment'}`}
          aria-label={`View ${field.Label || 'attachment'}`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
        </button>
      );
    }
    
    return formatDisplayValue(value);
  }, [formatDisplayValue]);

  // Get label for a field key
  const getFieldLabel = useCallback((key: string): string => {
    // Try to find matching field in visibleFields
    const field = safeVisibleFields.find(
      f => (f.Json_Attribute || f.BindValue) === key || 
           (f.Json_Attribute || f.BindValue)?.toLowerCase() === key.toLowerCase()
    );
    if (field?.Label) return field.Label;
    
    // Format key as readable label
    return key
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }, [safeVisibleFields]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current || !hasMore || isLoadingMore || !onLoadMore) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      // Trigger when user scrolls to within 200px of bottom
      if (scrollHeight - scrollTop - clientHeight < 200) {
        onLoadMore();
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  // Get primary fields - use first 3 fields as primary (or first field if only one)
  const primaryFields = safeVisibleFields.slice(0, Math.min(3, safeVisibleFields.length));

  // Get date fields
  const dateFields = safeVisibleFields.filter(f => {
    const label = (f.Label || "").toLowerCase();
    const controlType = (f.ControlType || "").toLowerCase();
    return label.includes("date") || controlType === "date" || controlType === "datetime";
  });

  // Get other fields (excluding primary and date fields)
  const otherFields = safeVisibleFields.filter((f, index) => {
    const label = (f.Label || "").toLowerCase();
    const controlType = (f.ControlType || "").toLowerCase();
    return index >= 3 && 
           !label.includes("date") && 
           controlType !== "date" && 
           controlType !== "datetime";
  });

  // Created-by field (shown on card when present in config)
  const createdByField = safeVisibleFields.find(
    (f) => (f.Label || "").toLowerCase() === "created by"
  );

  // Get current status label and color
  const getStatusInfo = (row: Record<string, any>, workflowStatus: ReturnType<typeof getWorkflowStatus>) => {
    const lastStateRaw =
      row?.last_state ??
      row?.lastState ??
      row?.LastState ??
      row?.workflow_state ??
      row?.workflowState;

    const normalizedLastState =
      typeof lastStateRaw === "string"
        ? lastStateRaw.trim().toLowerCase()
        : lastStateRaw !== undefined && lastStateRaw !== null
          ? String(lastStateRaw).trim().toLowerCase()
          : "";

    let statusLabel = "Not Started";
    let statusClass = "bg-gray-100 text-gray-600";

    if (workflowStatus.currentIndex >= 0 && workflowStatus.currentIndex < safeWorkflowActions.length) {
      const currentItem = safeWorkflowActions[workflowStatus.currentIndex];
      statusLabel = currentItem.Label || "In Progress";
      
      if (normalizedLastState.includes("inspected")) {
        statusClass = "bg-orange-100 text-orange-700";
      } else if (normalizedLastState.includes("approved") || normalizedLastState.includes("approve")) {
        statusClass = "bg-green-100 text-green-700";
      } else if (normalizedLastState.includes("estimated")) {
        statusClass = "bg-blue-100 text-blue-700";
      } else if (normalizedLastState.includes("invoiced")) {
        statusClass = "bg-purple-100 text-purple-700";
      } else if (normalizedLastState.includes("closed")) {
        statusClass = "bg-emerald-100 text-emerald-700";
      }
    }

    return { statusLabel, statusClass };
  };

  // Get next action button
  const getNextAction = (workflowStatus: ReturnType<typeof getWorkflowStatus>) => {
    if (workflowStatus.nextIndex >= 0 && workflowStatus.nextIndex < safeWorkflowActions.length) {
      return safeWorkflowActions[workflowStatus.nextIndex];
    }
    return null;
  };

  // Calculate display count
  const displayCount = filteredData.length;
  const totalDisplayCount = useServerSidePagination ? totalCount : safeData.length;
  const progressPercentage = totalDisplayCount > 0 ? (displayCount / totalDisplayCount) * 100 : 0;

  // Get searchable fields - use all visible fields for search
  const searchableFields = safeVisibleFields;
  
  // Update ref when selectedSearchField changes
  useEffect(() => {
    selectedSearchFieldRef.current = selectedSearchField;
  }, [selectedSearchField]);

  // Initialize selected search field ONLY ONCE on mount
  // This should never run again after initialization
  useEffect(() => {
    // Only initialize if we haven't initialized yet and fields are available
    if (!initializedRef.current && safeVisibleFields.length > 0) {
      // First, try to sync from filters if available
      if (filters && Object.keys(filters).length > 0) {
        const activeFilterKey = Object.keys(filters).find(key => filters[key]);
        if (activeFilterKey) {
          // Check if the active filter key is a valid field
          const isValidField = safeVisibleFields.some(
            f => (f.Json_Attribute || f.BindValue) === activeFilterKey
          );
          if (isValidField) {
            setSelectedSearchField(activeFilterKey);
            selectedSearchFieldRef.current = activeFilterKey;
            initializedRef.current = true;
            return; // Exit early if we set from filters
          }
        }
      }
      
      // If no filter match, use first available field
      const firstField = safeVisibleFields[0];
      const fieldKey = firstField?.Json_Attribute || firstField?.BindValue || "";
      if (fieldKey) {
        setSelectedSearchField(fieldKey);
        selectedSearchFieldRef.current = fieldKey;
      }
      
      initializedRef.current = true;
    }
  }, [safeVisibleFields, filters]); // Only run when fields or filters change, but guarded by initializedRef

  // Sync local search value with filters when selectedSearchField or filters change externally
  useEffect(() => {
    if (selectedSearchField) {
      const filterValue = filters?.[selectedSearchField] || "";
      setLocalSearchValue(filterValue);
    }
  }, [selectedSearchField, filters]);

  // Debounced filter change handler
  const handleSearchChange = useCallback((value: string) => {
    // Update local state immediately for responsive UI
    setLocalSearchValue(value);
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce the actual filter change (300ms delay)
    debounceTimerRef.current = setTimeout(() => {
      if (onFilterChange && selectedSearchField) {
        onFilterChange(selectedSearchField, value);
      }
    }, 300);
  }, [onFilterChange, selectedSearchField]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="flex flex-col h-full min-h-0 bg-gray-50 dark:bg-gray-900">
      {/* Enhanced Search Bar - Always visible, outside card container */}
      {onFilterChange && searchableFields.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex-shrink-0 shadow-sm z-10 sticky top-0">
          <div className="flex items-center gap-3">
            {/* Field Selection Dropdown with Enhanced Styling */}
            <div className="relative flex-shrink-0">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                Search By
              </label>
              <div className="relative">
                <select
                  key={`search-field-select-${searchableFields.length}`}
                  value={selectedSearchField || ""}
                  onChange={(e) => {
                    e.stopPropagation(); // Prevent event bubbling
                    const newField = e.target.value;
                    console.log("Dropdown onChange fired. New value:", newField, "Current state:", selectedSearchField); // Debug log
                    
                    // Mark that user has manually selected a field and initialization is done
                    userSelectedFieldRef.current = true;
                    initializedRef.current = true;
                    
                    // Update ref immediately
                    selectedSearchFieldRef.current = newField;
                    
                    // Update state immediately - this should be synchronous
                    setSelectedSearchField(newField);
                    
                    console.log("State updated to:", newField); // Debug log
                    
                    // Clear local search value
                    setLocalSearchValue("");
                    
                    // Clear search when changing field (but do this after state update)
                    if (onFilterChange) {
                      // Use a small delay to ensure state has updated
                      setTimeout(() => {
                        // Clear previous field filter
                        Object.keys(filters || {}).forEach(key => {
                          if (key !== newField && filters?.[key]) {
                            onFilterChange(key, "");
                          }
                        });
                        // Clear new field filter
                        onFilterChange(newField, "");
                      }, 10);
                    }
                  }}
                  className="appearance-none px-4 py-2.5 pr-8 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all min-w-[180px] cursor-pointer hover:border-gray-400 dark:hover:border-gray-500"
                  title="Select field to search"
                >
                  {searchableFields.length === 0 ? (
                    <option value="">No fields available</option>
                  ) : (
                    searchableFields.map((field) => {
                      const fieldKey = field.Json_Attribute || field.BindValue || "";
                      const fieldLabel = field.Label || fieldKey || "Unknown";
                      return (
                        <option key={fieldKey} value={fieldKey}>
                          {fieldLabel}
                        </option>
                      );
                    })
                  )}
                </select>
                {/* Custom dropdown arrow */}
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400 dark:text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Search Input with Clear Button */}
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                Search Value
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400 dark:text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={selectedSearchField ? `Type to search ${(searchableFields.find(f => (f.Json_Attribute || f.BindValue) === selectedSearchField)?.Label || selectedSearchField || "").toLowerCase()}...` : "Search..."}
                  value={localSearchValue}
                  onChange={(e) => {
                    handleSearchChange(e.target.value);
                  }}
                  className="block w-full pl-10 pr-10 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                />
                {/* Clear button */}
                {selectedSearchField && localSearchValue && (
                  <button
                    type="button"
                    onClick={() => {
                      setLocalSearchValue("");
                      if (onFilterChange && selectedSearchField) {
                        // Clear immediately when clicking clear button
                        if (debounceTimerRef.current) {
                          clearTimeout(debounceTimerRef.current);
                        }
                        onFilterChange(selectedSearchField, "");
                      }
                    }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none transition-colors"
                    title="Clear search"
                    aria-label="Clear search"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable Card Grid Container */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar min-h-0"
        style={{ minHeight: 0 }}
      >
        {/* Show loading state only when initially loading (not when loading more) */}
        {isLoadingMore && safeData.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-10 text-center min-h-[400px]">
            <div className="inline-block w-8 h-8 border-4 border-gray-200 dark:border-gray-700 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        ) : filteredData.length === 0 ? (
          /* No results - search bar is still visible above */
          <div className="flex flex-col items-center justify-center p-10 text-center min-h-[400px]">
            <div className="bg-primary-50 dark:bg-primary-900/30 rounded-full p-3 mb-3">
              <svg
                className="h-8 w-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: "var(--color-primary-500, #35ba5b)" }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            </div>
            <h3 className="text-gray-900 dark:text-gray-100 font-medium text-lg">No Data Available</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 max-w-sm">
              {Object.keys(filters || {}).some(k => filters?.[k]) ? "No records found matching your search." : "No records found at this time."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 p-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
            {filteredData.map((row, index) => {
              try {
                const workflowStatus = getWorkflowStatus(row);
                const cardProgressPercentage = getProgressPercentage(workflowStatus.completedIndices, safeWorkflowActions.length);
                const isSelected = selectedRowIndex === index;
                const statusInfo = getStatusInfo(row, workflowStatus);
                const nextAction = getNextAction(workflowStatus);
                const currentStep = workflowStatus.currentIndex + 1;
                const totalSteps = safeWorkflowActions.length;

                // Get primary field values (first field as ID, second as title, third as subtitle)
                const primaryField1 = primaryFields[0];
                const primaryField2 = primaryFields[1];
                const primaryField3 = primaryFields[2];
                
                const primaryValue1 = primaryField1 ? getFieldValue(row, primaryField1) : "";
                const primaryValue2 = primaryField2 ? getFieldValue(row, primaryField2) : "";
                const primaryValue3 = primaryField3 ? getFieldValue(row, primaryField3) : "";

            // Get date values - exclude dates already shown as primary fields
            const primaryFieldKeys = [primaryField1, primaryField2, primaryField3]
              .filter(Boolean)
              .map(f => f?.Json_Attribute || f?.BindValue);
            const remainingDateFields = dateFields.filter(df => 
              !primaryFieldKeys.includes(df.Json_Attribute) && 
              !primaryFieldKeys.includes(df.BindValue)
            );
            const dateValue1 = remainingDateFields.length > 0 ? getFieldValue(row, remainingDateFields[0]) : "";
            const dateValue2 = remainingDateFields.length > 1 ? getFieldValue(row, remainingDateFields[1]) : "";

            // Get other field for meta grid
            const otherField1 = otherFields.length > 0 ? otherFields[0] : null;
            const otherField2 = otherFields.length > 1 ? otherFields[1] : null;

            return (
              <div
                key={row.id || index}
                className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm transition-all cursor-pointer flex flex-col gap-1.5 p-2.5 ${
                  isSelected
                    ? "ring-2 ring-primary-500 dark:ring-primary-400 shadow-md"
                    : "hover:shadow-md hover:-translate-y-0.5"
                }`}
                onClick={() => handleCardClick(row, index)}
              >
                {/* Card Header: First Field as ID + Status Badge */}
                <div className="flex justify-between items-center gap-2">
                  <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded truncate flex-1 min-w-0">
                    {primaryField1?.Label ? `${primaryField1.Label}: ` : ""}{primaryValue1 || "N/A"}
                  </span>
                  {safeWorkflowActions.length > 0 && (
                    <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${statusInfo.statusClass}`}>
                      {statusInfo.statusLabel}
                    </span>
                  )}
                </div>

                {/* Card Body: Second and Third Fields */}
                {primaryValue2 && (
                  <div className="flex flex-col gap-0.5">
                    <div className="text-[13px] font-bold text-gray-900 dark:text-gray-100 truncate">
                      {primaryValue2}
                    </div>
                    {/* Show Total credit and Total debit side by side horizontally */}
                    {(primaryValue3 && primaryField3?.Label?.toLowerCase().includes("total credit")) || 
                     (otherField1 && otherField1.Label?.toLowerCase().includes("total debit")) ? (
                      <div className="flex items-center gap-4 text-[13px] text-gray-700 dark:text-gray-300">
                        {/* Total credit */}
                        {primaryValue3 && primaryField3?.Label?.toLowerCase().includes("total credit") && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">{primaryField3.Label}:</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                              {renderFieldValue(row, primaryField3)}
                            </span>
                          </div>
                        )}
                        {/* Total debit */}
                        {otherField1 && otherField1.Label?.toLowerCase().includes("total debit") && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">{otherField1.Label}:</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                              {renderFieldValue(row, otherField1)}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {primaryValue3 && (
                          <div className="text-[11px] text-gray-600 dark:text-gray-400 truncate">
                            {primaryValue3}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Meta Grid: Dates, Other Info, and Created By - Compact display */}
                {(dateValue1 || dateValue2 || otherField1 || otherField2 || createdByField) && (
                  <div className="grid grid-cols-2 gap-1 text-[9px] text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-1.5">
                    {dateValue1 && (
                      <div className="truncate" title={dateValue1}>
                        {dateValue1}
                      </div>
                    )}
                    {dateValue2 && (
                      <div className="truncate" title={dateValue2}>
                        {dateValue2}
                      </div>
                    )}
                    {/* Only show otherField1 in meta grid if it's not "Total debit" or "Total credit" (since they're shown above with values) */}
                    {otherField1 && !dateValue2 && 
                     !otherField1.Label?.toLowerCase().includes("total debit") && 
                     !(primaryValue3 && primaryField3?.Label?.toLowerCase().includes("total credit")) && (
                      <div className="truncate flex items-center gap-1" title={`${otherField1.Label}: ${getFieldValue(row, otherField1)}`}>
                        <span>{otherField1.Label}:</span>
                        {renderFieldValue(row, otherField1)}
                      </div>
                    )}
                    {otherField2 && (
                      <div className={`truncate flex items-center gap-1 ${dateValue1 && dateValue2 ? 'col-span-2' : ''}`} title={`${otherField2.Label}: ${getFieldValue(row, otherField2)}`}>
                        <span>{otherField2.Label}:</span>
                        {renderFieldValue(row, otherField2)}
                      </div>
                    )}
                    {createdByField && (
                      <div className="truncate flex items-center gap-1 col-span-2" title={`${createdByField.Label}: ${getFieldValue(row, createdByField)}`}>
                        <span>{createdByField.Label}:</span>
                        {renderFieldValue(row, createdByField)}
                      </div>
                    )}
                  </div>
                )}

                {/* Card Footer: Progress + Action Buttons */}
                <div className="mt-auto flex flex-col gap-1.5">
                  {/* Progress Container - Only show if workflow actions exist */}
                  {safeWorkflowActions.length > 0 && (
                    <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400">
                      <span className="whitespace-nowrap">Step {currentStep}/{totalSteps}</span>
                      <div 
                        className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onProgressBarClick) {
                            onProgressBarClick(row, e.currentTarget);
                          }
                        }}
                        title="Click to view timeline"
                      >
                        <div
                          ref={(el) => {
                            if (el) progressBarRefs.current.set(index, el);
                          }}
                          className="h-full rounded-full transition-all duration-400"
                          style={{ 
                            width: `${cardProgressPercentage}%`,
                            backgroundColor: cardProgressPercentage >= 40 ? "#27ae60" : "#e67e22"
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Workflow Action Button */}
                  {nextAction && (() => {
                    // Disable Close action if issue_status is "Pending"
                    const isCloseAction = nextAction.Action === "Close" || nextAction.Name === "Close";
                    const issueStatus = row["issue_status"] || row["issueStatus"] || row["ISSUE_STATUS"];
                    const isDisabled = isCloseAction && (issueStatus === "Pending" || String(issueStatus).toLowerCase() === "pending");
                    
                    return (
                      <button
                        className={`w-full py-1.5 px-2 border border-green-600 dark:border-green-500 bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 rounded-md font-semibold text-xs text-center transition-all ${
                          isDisabled 
                            ? "opacity-50 cursor-not-allowed" 
                            : "hover:bg-green-600 hover:text-white dark:hover:bg-green-500 dark:hover:text-white"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isDisabled) {
                            onActionTrigger?.(nextAction.Action, row, nextAction);
                          }
                        }}
                        disabled={isDisabled}
                        title={isDisabled && isCloseAction ? "Cannot close jobcard when part issue is pending" : undefined}
                      >
                        {nextAction.Label} &gt;
                      </button>
                    );
                  })()}

                  {/* Summary Actions (Edit, Delete, View, etc.) */}
                  {summaryActions.length > 0 && (
                    <div className="flex items-center justify-end gap-1.5">
                      {summaryActions.map((summaryAction, idx) => {
                        // Role-based visibility support (screen JSON uses `visibility: [roleIds...]`; support typo "visiblity")
                        const actionVisibility = ((summaryAction as any).visibility ?? (summaryAction as any).visiblity) as number[] | undefined;
                        const legacyVisibleForRoles = (summaryAction as any).visibleForRoles as number[] | undefined;
                        if (!isComponentVisible(actionVisibility, legacyVisibleForRoles)) {
                          return null;
                        }

                        const actionId =
                          summaryAction.Action?.Action ||
                          summaryAction.ActionType;
                        const buttonLabel =
                          summaryAction.ActionType ||
                          actionId ||
                          "Action";
                        const actionTypeValue = String(
                          summaryAction.ActionType ?? ""
                        );
                        const actionTypeLower = actionTypeValue
                          .trim()
                          .toLowerCase();
                        const iconSize = 14;
                        const icon = getSummaryActionIcon(
                          actionTypeLower,
                          iconSize
                        );

                        if (!actionId) {
                          return null;
                        }

                        // Status-based visibility support (e.g. visibleForStatus: ["pending"])
                        const visibleForStatus = (summaryAction as any).visibleForStatus as string[] | undefined;
                        if (Array.isArray(visibleForStatus) && visibleForStatus.length > 0) {
                          const rowStatusRaw =
                            (row as any).status ??
                            (row as any).current_state ??
                            (row as any).currentStatus ??
                            "";
                          const rowStatus = String(rowStatusRaw).trim().toLowerCase();
                          const allowed = visibleForStatus
                            .map((s) => String(s).trim().toLowerCase())
                            .includes(rowStatus);
                          if (!allowed) {
                            return null;
                          }
                        }

                        // Determine border and icon colors based on action type - outlined style (same as DataTable)
                        let borderColorClass = "border-green-500";
                        let iconColorClass = "text-green-500";
                        let hoverBgClass = "hover:bg-green-50 dark:hover:bg-green-900/20";
                        
                        if (actionTypeLower === "edit") {
                          borderColorClass = "border-green-500";
                          iconColorClass = "text-green-500";
                          hoverBgClass = "hover:bg-green-50 dark:hover:bg-green-900/20";
                        } else if (actionTypeLower === "delete") {
                          borderColorClass = "border-red-500";
                          iconColorClass = "text-red-500";
                          hoverBgClass = "hover:bg-red-50 dark:hover:bg-red-900/20";
                        } else if (actionTypeLower === "pdf_download" || actionTypeLower === "print" || actionTypeLower === "view_report") {
                          borderColorClass = "border-blue-500";
                          iconColorClass = "text-blue-500";
                          hoverBgClass = "hover:bg-blue-50 dark:hover:bg-blue-900/20";
                        } else if (actionTypeLower === "lock") {
                          borderColorClass = "border-orange-500";
                          iconColorClass = "text-orange-500";
                          hoverBgClass = "hover:bg-orange-50 dark:hover:bg-orange-900/20";
                        } else if (actionTypeLower === "approve") {
                          borderColorClass = "border-emerald-500";
                          iconColorClass = "text-emerald-600";
                          hoverBgClass = "hover:bg-emerald-50 dark:hover:bg-emerald-900/20";
                        }

                        return (
                          <React.Fragment key={`summary-${summaryAction.ActionType || idx}`}>
                            <button
                              className={`flex items-center justify-center w-7 h-7 bg-white dark:bg-gray-800 border ${borderColorClass} ${iconColorClass} rounded transition-all focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none active:scale-95 ${hoverBgClass}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const actionPayload = {
                                  ...row,
                                  __summaryAction: summaryAction,
                                };
                                onActionTrigger?.(actionId, actionPayload);
                              }}
                              title={buttonLabel || `Click to ${actionTypeLower}`}
                              aria-label={buttonLabel || `Click to ${actionTypeLower}`}
                              {...(actionTypeLower === "pdf_download" || actionTypeLower === "print" || actionId === "pdf" ? { "data-pdf-button": "true", "data-action-id": actionId } : {})}
                              {...(actionTypeLower === "edit" ? { "data-edit-button": "true", "data-action-id": actionId } : {})}
                              {...(actionTypeLower === "delete" ? { "data-delete-button": "true", "data-action-id": actionId } : {})}
                            >
                              {icon && (
                                <span className="flex items-center justify-center">
                                  {icon}
                                </span>
                              )}
                            </button>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              );
              } catch (error) {
                console.error("Error rendering card:", error, row);
                return (
                  <div key={row.id || index} className="bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-700 p-3">
                    <p className="text-xs text-red-600 dark:text-red-400">Error rendering card</p>
                  </div>
                );
              }
            })}
          </div>
        )}

        {/* Loading More Indicator */}
        {isLoadingMore && (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-3">
              <div className="inline-block w-6 h-6 border-3 border-gray-200 dark:border-gray-700 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Loading more records...</span>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Footer with Progress Bar */}
      <div className="sticky bottom-0 left-0 right-0 px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 z-30 mt-auto">
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing <span className="font-bold text-gray-800 dark:text-gray-200">
              {displayCount}
            </span> of <span className="font-bold text-gray-800 dark:text-gray-200">
              {totalDisplayCount}
            </span> records
          </p>
          <div className="flex-1 mx-4">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
              <div
                className="bg-primary-600 dark:bg-primary-500 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${progressPercentage}%`
                }}
              />
            </div>
          </div>
          {hasMore && !isLoadingMore && (
            <span className="text-xs text-gray-500 dark:text-gray-400">Scroll for more</span>
          )}
        </div>
      </div>

      {/* Detail Drawer - Portal */}
      {typeof document !== "undefined" && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className={`fixed inset-0 bg-black/50 z-[9998] transition-opacity duration-300 ${
              drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            onClick={handleDrawerClose}
          />
          
          {/* Drawer */}
          <div 
            className={`fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl z-[9999] transform transition-transform duration-300 ease-out flex flex-col ${
              drawerOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
                  <svg 
                    className="w-5 h-5 text-primary-600 dark:text-primary-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Record Details
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    View all information
                  </p>
                </div>
              </div>
              <button
                onClick={handleDrawerClose}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Close drawer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
              {drawerData && (
                <div className="space-y-4">
                  {/* Primary Fields Section */}
                  {primaryFields.length > 0 && (
                    <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4 border border-primary-100 dark:border-primary-800">
                      <h3 className="text-xs font-semibold text-primary-700 dark:text-primary-300 uppercase tracking-wide mb-3">
                        Primary Information
                      </h3>
                      <div className="space-y-3">
                        {primaryFields.map((field, idx) => {
                          const key = field.Json_Attribute || field.BindValue || "";
                          if (!key) return null;
                          return (
                            <div key={idx} className="flex flex-col">
                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
                                {field.Label || getFieldLabel(key)}
                              </span>
                              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                                {renderFieldValue(drawerData, field)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* All Fields Section */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-t-lg">
                      All Details
                    </h3>
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {Object.entries(drawerData)
                        .filter(([key]) => {
                          // Filter out internal/technical fields
                          const lowerKey = key.toLowerCase();
                          return !lowerKey.startsWith("__") && 
                                 !lowerKey.endsWith("_id") &&
                                 lowerKey !== "id" &&
                                 !lowerKey.includes("uuid") &&
                                 !lowerKey.includes("guid");
                        })
                        .map(([key, value], idx) => {
                          const field = safeVisibleFields.find(
                            f => (f.Json_Attribute || f.BindValue) === key || 
                                 (f.Json_Attribute || f.BindValue)?.toLowerCase() === key.toLowerCase()
                          );
                          return (
                            <div key={idx} className="flex items-start justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 flex-shrink-0 w-1/3">
                                {getFieldLabel(key)}
                              </span>
                              <span className="text-sm text-gray-900 dark:text-gray-100 text-right flex-1 ml-3 break-words flex items-center justify-end">
                                {field ? renderFieldValue(drawerData, field) : formatDisplayValue(value)}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* Workflow Status Section */}
                  {safeWorkflowActions.length > 0 && drawerData && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-t-lg">
                        Workflow Progress
                      </h3>
                      <div className="p-4">
                        {(() => {
                          const workflowStatus = getWorkflowStatus(drawerData);
                          const statusInfo = getStatusInfo(drawerData, workflowStatus);
                          const progressPct = getProgressPercentage(workflowStatus.completedIndices, safeWorkflowActions.length);
                          
                          return (
                            <div className="space-y-4">
                              {/* Status Badge */}
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Current Status:</span>
                                <span className={`text-xs font-semibold uppercase px-2 py-1 rounded-full ${statusInfo.statusClass}`}>
                                  {statusInfo.statusLabel}
                                </span>
                              </div>
                              
                              {/* Progress Bar */}
                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                  <span>Progress</span>
                                  <span>{progressPct}%</span>
                                </div>
                                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full rounded-full transition-all duration-300"
                                    style={{ 
                                      width: `${progressPct}%`,
                                      backgroundColor: progressPct >= 40 ? "#27ae60" : "#e67e22"
                                    }}
                                  />
                                </div>
                              </div>

                              {/* Workflow Steps */}
                              <div className="space-y-2">
                                {safeWorkflowActions.map((action, idx) => {
                                  const status = getStepStatus(idx, workflowStatus.completedIndices, workflowStatus.currentIndex, workflowStatus.nextIndex);
                                  return (
                                    <div 
                                      key={idx} 
                                      className={`flex items-center gap-3 p-2 rounded-lg ${
                                        status === "completed" 
                                          ? "bg-green-50 dark:bg-green-900/20" 
                                          : status === "active"
                                            ? "bg-orange-50 dark:bg-orange-900/20"
                                            : "bg-gray-50 dark:bg-gray-700/50"
                                      }`}
                                    >
                                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                        status === "completed" 
                                          ? "bg-green-500 text-white" 
                                          : status === "active"
                                            ? "bg-orange-500 text-white"
                                            : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                                      }`}>
                                        {status === "completed" ? (
                                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                          </svg>
                                        ) : (
                                          <span className="text-xs font-bold">{idx + 1}</span>
                                        )}
                                      </div>
                                      <span className={`text-sm ${
                                        status === "completed" 
                                          ? "text-green-700 dark:text-green-300 font-medium" 
                                          : status === "active"
                                            ? "text-orange-700 dark:text-orange-300 font-medium"
                                            : "text-gray-500 dark:text-gray-400"
                                      }`}>
                                        {action.Label || action.Action}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            <div className="flex-shrink-0 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={handleDrawerClose}
                className="w-full py-2.5 px-4 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium text-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Close
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
